import { translate } from "@/plugins/i18n";
import type BaseEnumType from "../../baseEnumType";
import Key from "@/app/types/common/key";
import { Maybe } from "@/components/utils/typeHelpers";


export class EnumDocumentContextType implements BaseEnumType {
  private static allValues: EnumDocumentContextType[] = []; // make sure this is top-most
  
  public static outlet = new EnumDocumentContextType(0, "Outlet", "enum_document_context_type_outlet");
  public static supplierManufacturer = new EnumDocumentContextType(1, "SupplierManufacturer", "enum_document_context_type_supplier_manufacturer");
  public static stockItem = new EnumDocumentContextType(2, "StockItem", "enum_document_context_type_stock_item");
  public static lead = new EnumDocumentContextType(3, "Lead", "enum_document_context_type_lead");
  public static order = new EnumDocumentContextType(4, "Order", "enum_document_context_type_order");
  public static credit = new EnumDocumentContextType(5, "Credit", "enum_document_context_type_credit");
  public static outletGroup = new EnumDocumentContextType(6, "OutletGroup", "enum_document_context_type_outlet_group");
  public static purchaseOrder = new EnumDocumentContextType(7, "PurchaseOrder", "enum_document_context_type_purchase_order");
  public static stockTake = new EnumDocumentContextType(8, "StockTake", "enum_document_context_type_stock_take");
  public static stockTransfer = new EnumDocumentContextType(9, "StockTransfer", "enum_document_context_type_stock_transfer");
  public static stockAdjustment = new EnumDocumentContextType(10, "StockAdjustment", "enum_document_context_type_stock_adjustment");
  public static productBrand = new EnumDocumentContextType(11, "ProductBrand", "enum_document_context_type_product_brand");
  public static assembly = new EnumDocumentContextType(12, "Assembly", "enum_document_context_type_assembly");
  public static process = new EnumDocumentContextType(13, "Process", "enum_document_context_type_process");
  public static batch = new EnumDocumentContextType(14, "Batch", "enum_document_context_type_batch");
  public static recipe = new EnumDocumentContextType(15, "Recipe", "enum_document_context_type_recipe");
  
  public key: number;
  public value: string;
  public translationKey: string;

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumDocumentContextType.allValues.push(this);
  }

  public get translation() { return translate(Key.fromString(this.translationKey)); }

  public isOutlet() { return this === EnumDocumentContextType.outlet; }
  public isSupplierManufacturer() { return this === EnumDocumentContextType.supplierManufacturer; }
  public isStockItem() { return this === EnumDocumentContextType.stockItem; }
  public isLead() { return this === EnumDocumentContextType.lead; }
  public isOrder() { return this === EnumDocumentContextType.order; }
  public isCredit() { return this === EnumDocumentContextType.credit; }
  public isOutletGroup() { return this === EnumDocumentContextType.outletGroup; }
  public isPurchaseOrder() { return this === EnumDocumentContextType.purchaseOrder; }
  public isStockTake() { return this === EnumDocumentContextType.stockTake; }
  public isStockTransfer() { return this === EnumDocumentContextType.stockTransfer; }
  public isStockAdjustment() { return this === EnumDocumentContextType.stockAdjustment; }
  public isProductBrand() { return this === EnumDocumentContextType.productBrand; }
  public isAssembly() { return this === EnumDocumentContextType.assembly; }
  public isProcess() { return this === EnumDocumentContextType.process; }
  public isBatch() { return this === EnumDocumentContextType.batch; }
  public isRecipe() { return this === EnumDocumentContextType.recipe; }

  public static getValues(): readonly EnumDocumentContextType[] {
    return EnumDocumentContextType.allValues;
  }

  public static getValuesExcept0(): readonly EnumDocumentContextType[] {
    return EnumDocumentContextType.allValues.filter((x) => x.key !== 0);
  }

  public static getByKey(key: number): EnumDocumentContextType {
    const found = EnumDocumentContextType.allValues.find(e => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumDocumentContextType - Key not found: ${ key }`);
    }

    return found;
  }

  public static getByValue(name: string, def?: EnumDocumentContextType): EnumDocumentContextType {
    const found = name ? EnumDocumentContextType.allValues.find(e => e.value.toLowerCase() === name.toLowerCase()) : null;
    if (!found) {
      if (def) {
        return def;
      }
      throw new Error(`EnumDocumentContextType - Value not found: ${ name }`);
    }

    return found;
  }

  public equals(other: Maybe<EnumDocumentContextType>): boolean {
    return this.key === other?.key;
  }
}